import { isInsideEditorPreviewFrame } from '@Core/lib/pageEditorPreview';
import { usePage } from '@inertiajs/react';
import type { ButtonProps } from '@Pages/pageRuntime/components/ui/Button';
import { buttonVariants } from '@Pages/pageRuntime/components/ui/buttonVariants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@Pages/pageRuntime/components/ui/DropdownMenu';
import { SidebarMenuButton } from '@Pages/pageRuntime/components/ui/Sidebar';
import { useIsInSidebar, useSidebar } from '@Pages/pageRuntime/components/ui/useSidebar';
import { usePermissions } from '@Pages/pageRuntime/hooks/usePermissions';
import { cn } from '@Pages/pageRuntime/lib/utils';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import LockIcon from 'lucide-react/dist/esm/icons/lock';
import Pencil from 'lucide-react/dist/esm/icons/pencil';
import Settings from 'lucide-react/dist/esm/icons/settings';

import type { PageProps } from '@/types';
import { SystemPermission } from '@/types/enums';

import { buildEditorHref } from './buildEditorHref';

type AdminNavButtonsPageProps = PageProps<{
  pageId?: string;
  pageRouteSettings?: Record<string, Diagonal.Pages.Data.PageRouteSettingsData> | null;
}>;

type AdminNavButtonsProps = {
  /** Additional classes for the button group. */
  className?: string;

  /** Button visual style. */
  variant?: ButtonProps['variant'];

  /** Button size preset. */
  size?: ButtonProps['size'];
};

/**
 * `_top` breaks the admin build out of the editor's preview iframe, which would
 * otherwise load the editor inside its own preview. It is deliberately limited to
 * that frame: the public embed iframe allows user-activated top navigation, so a
 * `_top` link there would navigate the host site away to the admin build.
 */
function adminLinkTarget(): '_top' | undefined {
  return isInsideEditorPreviewFrame() ? '_top' : undefined;
}

function AdminSidebarDropdown({
  editorHref,
  linkTarget
}: {
  editorHref: string | null;
  linkTarget: '_top' | undefined;
}) {
  const { isMobile } = useSidebar();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton tooltip="Admin">
          <Settings className="size-4" />
          <span>Admin</span>
          <ChevronRight className="ml-auto size-4" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-48 rounded-md"
        side={isMobile ? 'bottom' : 'right'}
        align="end"
        sideOffset={4}
      >
        {editorHref ? (
          <DropdownMenuItem asChild>
            {/* Plain anchor (not Inertia <Link>) — the admin editor lives in a separate Vite build, so it must be loaded via a full page navigation. */}
            <a href={editorHref} target={linkTarget}>
              <Pencil />
              Edit Page
            </a>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem asChild>
          {/* Plain anchor (not Inertia <Link>) — the admin portal is a separate Vite build, so it must be loaded via a full page navigation. */}
          <a href="/admin" target={linkTarget}>
            <LockIcon />
            Visit Admin Portal
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Renders admin navigation controls for custom layouts: edit page and visit admin
 * portal. Inside a sidebar they render as a single "Admin" menu row opening a
 * dropdown (matching the built-in app sidebar); elsewhere as a row of buttons. Hidden
 * automatically for identities without the manage_pages permission.
 *
 * @example
 * <Sdk.Components.AdminNavButtons variant="outline" />
 */
export function AdminNavButtons({
  className,
  variant = 'ghost',
  size = 'default'
}: AdminNavButtonsProps) {
  const { hasPermission } = usePermissions();
  const { pageId, pageRouteSettings } = usePage<AdminNavButtonsPageProps>().props;
  const inSidebar = useIsInSidebar();

  if (!hasPermission(SystemPermission.ManagePages)) {
    return null;
  }

  const editorHref = pageId ? buildEditorHref(pageId, pageRouteSettings) : null;
  const linkTarget = adminLinkTarget();

  if (inSidebar) {
    return <AdminSidebarDropdown editorHref={editorHref} linkTarget={linkTarget} />;
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {editorHref ? (
        <a href={editorHref} target={linkTarget} className={buttonVariants({ variant, size })}>
          <Pencil className="size-4" />
          <span>Edit Page</span>
        </a>
      ) : null}
      <a href="/admin" target={linkTarget} className={buttonVariants({ variant, size })}>
        <LockIcon className="size-4" />
        <span>Admin Portal</span>
      </a>
    </div>
  );
}
